import React, { useEffect, useRef, useState } from "react";
import { request } from "./api";
interface Access {
  version: number;
  receptionEnabled: boolean;
  changedAt: string | null;
  changedBy: string | null;
}
export function AccessSettings() {
  const [access, setAccess] = useState<Access | null>(null),
    [enabled, setEnabled] = useState(false),
    [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false);
  const retry = useRef<{ json: string; key: string } | null>(null);
  useEffect(() => {
    request<Access>("/access")
      .then((a) => {
        setAccess(a);
        setEnabled(a.receptionEnabled);
      })
      .catch((e) => setError(e.message));
  }, []);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!access) return;
    setBusy(true);
    setError("");
    setSaved(false);
    const body = {
      expectedVersion: access.version,
      receptionEnabled: enabled,
      reason,
    };
    const json = JSON.stringify(body);
    if (retry.current?.json !== json)
      retry.current = { json, key: crypto.randomUUID() };
    try {
      const result = await request<Access>("/access", {
        ...body,
        idempotencyKey: retry.current.key,
      });
      setAccess(result);
      setEnabled(result.receptionEnabled);
      setSaved(true);
      window.dispatchEvent(new Event("housekeeping-settings-changed"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={save} className="pfres-section">
      <div className="pfres-section__header">
        <h2 className="pfres-section__title">Reception access</h2>
        <p className="pfres-section__lede">
          Enable reception to use daily tasks, actions, reports, history and
          print. Settings remain restricted to managers and superadmins. Changes
          take effect immediately on the next request.
        </p>
      </div>
      {access ? (
        <>
          <label className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={enabled}
              disabled={busy}
              onChange={(e) => {
                setEnabled(e.target.checked);
                setSaved(false);
              }}
            />
            <span className="form-check-label">Enable for reception</span>
          </label>
          <label className="d-block my-2">
            Reason
            <input
              className="form-control"
              required
              maxLength={1000}
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button
            className="btn btn-outline-contrast"
            disabled={busy || enabled === access.receptionEnabled}
          >
            {busy ? "Saving..." : "Apply access change now"}
          </button>
          <small className="d-block mt-2">
            Version {access.version} ·{" "}
            {access.changedAt
              ? `Last changed ${access.changedAt} by ${access.changedBy}`
              : "Reception disabled by default"}
          </small>
        </>
      ) : (
        !error && <p role="status">Loading access settings...</p>
      )}
      {error && (
        <p className="text-danger" role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="text-success">
          Reception access updated.
        </p>
      )}
    </form>
  );
}
